import { type LogsStoreState } from '../../../store/insights/LogsStore';

const DEVICE_OWNER_URI_PREFIX = 'homey:device:';

type DeviceLogsIndex = {
  countsByDeviceId: Map<string, number>;
  logIdsByDeviceId: Map<string, string[]>;
};

const deviceLogsIndexCache = new WeakMap<object, DeviceLogsIndex>();

export function getNumberDeviceLogsCount({
  deviceId,
  logsById,
}: {
  deviceId: string | undefined;
  logsById: LogsStoreState['byId'];
}): number | null {
  if (deviceId == null || logsById == null) {
    return null;
  }

  const deviceLogsIndex = getDeviceLogsIndex(logsById);

  return deviceLogsIndex.countsByDeviceId.get(deviceId) ?? 0;
}

export function getNumberDeviceLogIds({
  deviceId,
  logsById,
}: {
  deviceId: string | undefined;
  logsById: LogsStoreState['byId'];
}): string[] {
  if (deviceId == null || logsById == null) {
    return [];
  }

  const deviceLogsIndex = getDeviceLogsIndex(logsById);

  return deviceLogsIndex.logIdsByDeviceId.get(deviceId) ?? [];
}

function getDeviceLogsIndex(logsById: NonNullable<LogsStoreState['byId']>): DeviceLogsIndex {
  const cachedDeviceLogsIndex = deviceLogsIndexCache.get(logsById);

  if (cachedDeviceLogsIndex != null) {
    return cachedDeviceLogsIndex;
  }

  const countsByDeviceId = new Map<string, number>();
  const logIdsByDeviceId = new Map<string, string[]>();

  for (const log of Object.values(logsById)) {
    if (log == null) {
      continue;
    }

    if (log.type !== 'number' || typeof log.ownerUri !== 'string') {
      continue;
    }

    if (log.ownerUri.startsWith(DEVICE_OWNER_URI_PREFIX) !== true) {
      continue;
    }

    const ownerDeviceId = log.ownerUri.slice(DEVICE_OWNER_URI_PREFIX.length);

    if (ownerDeviceId === '') {
      continue;
    }

    countsByDeviceId.set(ownerDeviceId, (countsByDeviceId.get(ownerDeviceId) ?? 0) + 1);

    const logIds = logIdsByDeviceId.get(ownerDeviceId);

    if (logIds == null) {
      logIdsByDeviceId.set(ownerDeviceId, [log.id]);
      continue;
    }

    logIds.push(log.id);
  }

  const deviceLogsIndex = {
    countsByDeviceId,
    logIdsByDeviceId,
  };

  deviceLogsIndexCache.set(logsById, deviceLogsIndex);

  return deviceLogsIndex;
}
